"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.nativeLlmResponseText = exports.nativeLlmResponseTextResultSchema = exports.nativeLlmResponseTextArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Zod schema describing arbitrary JSON objects
var jsonValueSchema = zod_1.z.lazy(function () {
    return zod_1.z.union([
        zod_1.z.string(),
        zod_1.z.number(),
        zod_1.z.boolean(),
        zod_1.z.null(),
        zod_1.z.array(jsonValueSchema),
        zod_1.z.record(jsonValueSchema),
    ]);
});
var jsonObjectSchema = zod_1.z.record(jsonValueSchema);
// Only user for now
var LlmRole = ['user'];
// Argument schema
exports.nativeLlmResponseTextArgsSchema = zod_1.z.object({
    model: zod_1.z.enum([
        'gpt-5-mini',
        'gpt-5',
        'o4-mini',
        'gpt-5-nano',
        'gpt-4o-mini',
        'gpt-4o',
        'gpt-4.1',
        'gpt-4.1-mini',
    ]).describe("required: The OpenAI model to use for chat completion.\nList of supported models for nativeLlmResponseText:\n- 'gpt-5-mini', use for most tasks, extracting text out of images, and simple tasks (default)\n- 'gpt-5', use for complicated tasks requiring complex response schemas\n- 'o4-mini', use for deep/critical thinking\n\n"),
    userPrompts: zod_1.z
        .array(zod_1.z.object({
        role: zod_1.z.enum(LlmRole).describe('required: Must be "user"'),
        content: zod_1.z
            .array(zod_1.z.discriminatedUnion('type', [
            zod_1.z.object({
                type: zod_1.z.literal('text'),
                text: zod_1.z
                    .string()
                    .describe('required: The complete user prompt to send to the language model. Preserve the full content exactly as provided - do not truncate or summarize.'),
            }),
            zod_1.z.object({
                type: zod_1.z.literal('image_url'),
                image_url: zod_1.z.object({
                    url: zod_1.z
                        .string()
                        .url()
                        .describe('required: The url of the image to pass to the LLM.'),
                }),
            }),
        ]))
            .describe('required: Array of content items (text and/or images)'),
    }))
        .describe('required: Array of user message objects. Each object MUST have "role" (always "user") and "content" (array of content items). Example: [{"role":"user","content":[{"type":"text","text":"Analyze this data"}]}]'),
    responseSchema: zod_1.z.union([zod_1.z.string(), jsonObjectSchema]).describe("\nrequired: JSON Schema for the LLM response. May be provided as a stringified schema or as a JSON object.\nThe only valid top-level type is 'object'.\nExamples:\n- String: '{\"type\":\"object\",\"properties\":{\"items\":{\"type\":\"array\",\"items\":{\"type\":\"string\"}}}}'\n- Object: { type: 'object', properties: { items: { type: 'array', items: { type: 'string' } } } }\n"),
    renderingSchema: zod_1.z.array(common_1.RenderingConfigSchema).nullish().describe("\n  User-facing rendering schema for displaying llm response text outputs.\n  Choose how to display results. Include only outputs useful to the user, or omit entirely.\n\n  Template variables use square bracket syntax [[variable]]:\n  - Reference node outputs: [[node-id.output-param]]\n  - Current iteration index: [[index]]\n\n  Rendering types:\n  - 'html': Renders HTML content in iframe\n  - 'csv': Converts CSV to markdown table\n  - 'url': Displays as clickable link or file preview\n  - 'markdown': Raw markdown content\n  - 'list': Intelligent array iteration with markdown formatting\n  \n  List type:\n  - Finds first array in variables and iterates over it\n  - Each iteration creates one block with variables substituted\n  - First array is indexed; later arrays auto-convert:\n    * Array of strings/primitives \u2192 markdown list\n    * Array of uniform objects \u2192 markdown table\n  - Special variable [[index]] for current iteration (1-based)\n  \n  Examples:\n  \n  Simple list:\n  { type: 'list', content: '- [[items]]' }\n  // Output: \"- item1\\n- item2\\n- item3\"\n  \n  Complex list with nested data:\n  { type: 'list', content: '# Item [[index]]\\n\\n[[items.summary]]\\n\\n[[items.tags]]' }\n  // Tags array auto-converts to markdown list\n  \n  Array of objects becomes table:\n  { type: 'list', content: '[[users]]' }\n  // Auto-converts to: \"| name | age |\\n| Alice | 30 |\\n| Bob | 25 |\"\n  \n  Try to break down the results into small chunks to optimize the display for the user.\n  "),
    emitMode: common_1.emitModeSchema,
});
// Result schema
exports.nativeLlmResponseTextResultSchema = zod_1.z.any();
// Tool definition
exports.nativeLlmResponseText = (0, common_1.createEmitModeToolMetadata)({
    id: 'nativeLlmResponseText',
    displayName: 'LLM Response Text',
    displayDescription: "\nGenerate structured text responses using LLM. Use for summarizing, analyzing, or formatting data.\n",
    category: 'ai',
    color: common_1.BRAND_COLORS.AI,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.AI),
    icon: 'text-gen',
    argsSchema: exports.nativeLlmResponseTextArgsSchema,
}, exports.nativeLlmResponseTextResultSchema);
